import { TFunction } from 'i18next';

import { DOMAIN } from 'data/constants';
import {
  DepositMethod,
  DepositBank,
  DepositCredentials,
  DepositTransferType,
  isSbpMethod,
  isQRMethod,
} from 'data/payments/models';
import paymentInstructionCallouts from 'utils/gueds';

export type DeviceType = 'android' | 'ios' | 'desktop';

export type RawCallout = {
  id: string;
  x: number;
  y: number;
  description: string;
  markerPosition?: 'top' | 'bottom' | 'auto';
  title?: string;
  // Mobile-specific coordinates (for VND method)
  xMobile?: number;
  yMobile?: number;
};

export type PaymentInstructionCallouts = Record<
  string,
  | Record<string, Record<DeviceType, Record<string, RawCallout[]>>>
  | Record<DeviceType, Record<string, RawCallout[]>>
>;

export type ImageCallout = {
  id: string;
  x: number;
  y: number;
  title?: string;
  description: string;
  copyableData?: string | string[];
  markerPosition?: 'top' | 'bottom' | 'auto';
  // Mobile-specific coordinates (for VND method)
  xMobile?: number;
  yMobile?: number;
};

export type InstructionSlide =
  | {
      kind: 'image';
      src: string;
      alt: string;
      callouts?: ImageCallout[];
    }
  | {
      kind: 'text';
      title: string;
      description: string;
    };

const bankMap: Record<DepositBank, string> = {
  Сбербанк: 'sber',
  ВТБ: 'vtb',
  'Т-Банк': 'tbank',
  'МТС Банк': 'mts',
  'Альфа банк': 'alfa',
};

export function getS3ImageUrl(
  method: DepositMethod | null,
  device: DeviceType,
  bank: DepositBank | null,
  step?: number,
  transferType?: DepositTransferType,
): string {
  const assetsUrl = `https://payment.${DOMAIN}/assets`;

  if (method === 'MOBCOM') {
    const deviceMap: Record<DeviceType, string> = {
      android: 'android',
      ios: 'ios',
      desktop: 'desktop',
    };

    const bankName = bank ? bankMap[bank] : 'default';
    const deviceName = deviceMap[device];
    const stepSuffix = step ? `${step}` : '1';

    return `${assetsUrl}/mobcom/${bankName}/${deviceName}/${bankName}-mobcom-${deviceName}${stepSuffix}.png`;
  }

  if (method && isQRMethod(method)) {
    const deviceMap: Record<DeviceType, string> = {
      android: 'android',
      ios: 'ios',
      desktop: 'desktop',
    };
    const basePath = method === 'VND_TG_QR' ? 'vnd' : 'php';
    const bankName = bank ? bankMap[bank] : 'sber';
    const deviceName = deviceMap[device];
    const stepSuffix = step ? `${step}` : '1';
    return `${assetsUrl}/${basePath}/${bankName}/${deviceName}/${bankName}-${basePath}-${deviceName}${stepSuffix}.png`;
  }

  if (method === 'TJS_TG') {
    const deviceMap: Record<DeviceType, string> = {
      android: 'android',
      ios: 'ios',
      desktop: 'desktop',
    };

    const bankName = bank ? bankMap[bank] : 'default';
    const deviceName = deviceMap[device];

    const variant = transferType === 'CARD' ? 'card' : 'sbp';
    const stepSuffix = step ? `${step}` : '1';

    return `${assetsUrl}/tjs/${variant}/${bankName}/${deviceName}/${bankName}-tjs-${variant}-${deviceName}${stepSuffix}.png`;
  }

  let basePath = 'sbp';
  if (
    method === 'SBP' ||
    method === 'SBP_CLASSIC' ||
    method === 'SBP_GU' ||
    method === 'SBP_CA' ||
    method === 'SBP_MONO_YA' ||
    method === 'SBP_MONO_OZ'
  ) {
    basePath = 'sbp';
  } else if (method === 'AMD_TG_SBP') {
    basePath = 'amd-tg';
  }

  const deviceMap: Record<DeviceType, string> = {
    android: 'android',
    ios: 'ios',
    desktop: 'desktop',
  };

  const bankName = bank ? bankMap[bank] : 'default';
  const deviceName = deviceMap[device];

  const methodSuffix = basePath === 'amd-tg' ? 'amd' : 'sbp';
  const imageUrl = `${assetsUrl}/${basePath}/${bankName}/${deviceName}/${bankName}-${methodSuffix}-${deviceName}${step}.png`;
  return imageUrl;
}

export function getCalloutsFromJson(
  t: TFunction,
  method: DepositMethod | null,
  bank: DepositBank | null,
  device: DeviceType,
  step: number,
  credentials?: DepositCredentials | null,
  amount?: string | null,
  currency?: string | null,
  isMobile?: boolean,
): ImageCallout[] {
  if (!method) return [];

  if (method === 'MOBCOM' && bank) {
    const basePath = 'mobcom';

    const bankName = bankMap[bank];
    const stepKey = String(step);

    // Navigate JSON structure: mobcom -> bank -> device -> step
    const baseCallouts = paymentInstructionCallouts as Record<string, unknown>;
    const bankCallouts = (baseCallouts[basePath] as Record<string, unknown>)?.[
      bankName
    ] as Record<string, unknown> | undefined;
    const deviceCallouts = bankCallouts?.[device] as
      | Record<string, unknown>
      | undefined;
    const calloutsData = deviceCallouts?.[stepKey];

    if (!calloutsData || !Array.isArray(calloutsData)) {
      return [];
    }

    const interpolationVars: Record<string, string> = {};
    const dataValues: Record<string, string> = {};

    if (credentials) {
      const telValue = credentials.phoneNumber || credentials.value || '';
      interpolationVars.tel = telValue;
      dataValues.tel = telValue;

      const operatorValue = credentials.bank || '';
      interpolationVars.operator = operatorValue;
      // Keep compatibility with existing placeholders (some strings use {{bank}})
      interpolationVars.bank = operatorValue;
      dataValues.operator = operatorValue;
      dataValues.bank = operatorValue;
    }

    if (amount && currency) {
      const amountValue = `${amount} ${currency}`;
      interpolationVars.amount = amountValue;
      dataValues.amount = amountValue;
    } else if (amount) {
      interpolationVars.amount = amount;
      dataValues.amount = amount;
    }

    return calloutsData.map((callout) => {
      const descriptionKey = callout.description;
      const translatedDescription = t(
        descriptionKey,
        interpolationVars,
      ) as string;

      let copyableData: string | string[] | undefined;
      const descLower = translatedDescription.toLowerCase();

      const mentionsOperator =
        descLower.includes('оператор') || descLower.includes('operator');

      if (
        (descLower.includes('телефон') ||
          descLower.includes('номер') ||
          descLower.includes('phone')) &&
        dataValues.tel &&
        dataValues.tel.length > 0
      ) {
        copyableData = dataValues.tel;
      } else if (
        mentionsOperator &&
        dataValues.operator &&
        dataValues.operator.length > 0
      ) {
        copyableData = dataValues.operator;
      } else if (
        (descLower.includes('сумм') || descLower.includes('amount')) &&
        dataValues.amount &&
        dataValues.amount.length > 0
      ) {
        copyableData = dataValues.amount;
      }

      const useMobileCoords =
        isMobile &&
        callout.xMobile !== undefined &&
        callout.yMobile !== undefined;

      return {
        id: callout.id,
        x: useMobileCoords ? callout.xMobile ?? callout.x : callout.x,
        y: useMobileCoords ? callout.yMobile ?? callout.y : callout.y,
        xMobile: callout.xMobile,
        yMobile: callout.yMobile,
        title: callout.title
          ? (t(callout.title) as string | undefined) || undefined
          : undefined,
        description: translatedDescription,
        copyableData,
        markerPosition: callout.markerPosition,
      };
    });
  }

  if (isQRMethod(method)) {
    const basePath = method === 'VND_TG_QR' ? 'vnd' : 'php';
    const bankName = bank ? bankMap[bank] : 'sber';
    const stepKey = String(step);

    // Navigate JSON structure: vnd -> bank -> device -> step
    const baseCallouts = paymentInstructionCallouts as Record<string, unknown>;
    const bankCallouts = (baseCallouts[basePath] as Record<string, unknown>)?.[
      bankName
    ] as Record<string, unknown> | undefined;
    const deviceCallouts = bankCallouts?.[device] as
      | Record<string, unknown>
      | undefined;
    const calloutsData = deviceCallouts?.[stepKey];

    if (!calloutsData || !Array.isArray(calloutsData)) {
      return [];
    }

    const interpolationVars: Record<string, string> = {};
    const dataValues: Record<string, string> = {};

    if (credentials) {
      const qrValue = credentials.value || '';
      interpolationVars.qr = qrValue;
      dataValues.qr = qrValue;
    }

    if (amount && currency) {
      const amountValue = `${amount} ${currency}`;
      interpolationVars.amount = amountValue;
      dataValues.amount = amountValue;
    } else if (amount) {
      interpolationVars.amount = amount;
      dataValues.amount = amount;
    }

    return calloutsData.map((callout) => {
      const descriptionKey = callout.description;
      const translatedDescription = t(
        descriptionKey,
        interpolationVars,
      ) as string;

      let copyableData: string | string[] | undefined;

      const descLower = translatedDescription.toLowerCase();

      if (
        (descLower.includes('сумм') || descLower.includes('перевода')) &&
        dataValues.amount &&
        dataValues.amount.length > 0
      ) {
        copyableData = dataValues.amount;
      }

      // For mobile, use mobile-specific coordinates if available
      const useMobileCoords =
        isMobile &&
        callout.xMobile !== undefined &&
        callout.yMobile !== undefined;

      return {
        id: callout.id,
        x: useMobileCoords ? callout.xMobile ?? callout.x : callout.x,
        y: useMobileCoords ? callout.yMobile ?? callout.y : callout.y,
        xMobile: callout.xMobile,
        yMobile: callout.yMobile,
        title: callout.title
          ? (t(callout.title) as string | undefined) || undefined
          : undefined,
        description: translatedDescription,
        copyableData,
        markerPosition: callout.markerPosition,
      };
    });
  }

  if (method === 'TJS_TG' && bank) {
    const variant: 'sbp' | 'card' =
      credentials?.type === 'CARD' ? 'card' : 'sbp';
    const basePath = 'tjs';

    const bankName = bankMap[bank];
    const stepKey = String(step);

    // Navigate JSON structure: tjs -> (sbp|card) -> bank -> device -> step
    const baseCallouts = paymentInstructionCallouts as Record<string, unknown>;
    const variantCallouts = (
      baseCallouts[basePath] as Record<string, unknown>
    )?.[variant] as Record<string, unknown> | undefined;
    const bankCallouts = variantCallouts?.[bankName] as
      | Record<string, unknown>
      | undefined;
    const deviceCallouts = bankCallouts?.[device] as
      | Record<string, unknown>
      | undefined;
    const calloutsData = deviceCallouts?.[stepKey];

    if (!calloutsData || !Array.isArray(calloutsData)) {
      return [];
    }

    const interpolationVars: Record<string, string> = {};
    const dataValues: Record<string, string> = {};

    if (credentials) {
      const telValue = credentials.phoneNumber || '';
      interpolationVars.tel = telValue;
      dataValues.tel = telValue;

      const cardValue = credentials.cardNumber || credentials.value || '';
      interpolationVars.card = cardValue;
      dataValues.card = cardValue;

      const nameValue = credentials.name || '';
      interpolationVars.name = nameValue;
      dataValues.name = nameValue;

      const bankValue = credentials.bank || '';
      interpolationVars.bank = bankValue;
      dataValues.bank = bankValue;
    }

    if (amount && currency) {
      const amountValue = `${amount} ${currency}`;
      interpolationVars.amount = amountValue;
      dataValues.amount = amountValue;
    } else if (amount) {
      interpolationVars.amount = amount;
      dataValues.amount = amount;
    }

    return calloutsData.map((callout) => {
      const descriptionKey = callout.description;
      const translatedDescription = t(
        descriptionKey,
        interpolationVars,
      ) as string;

      let copyableData: string | string[] | undefined;
      const descLower = translatedDescription.toLowerCase();

      const mentionsName =
        descLower.includes('фио') ||
        descLower.includes('данные получателя') ||
        descLower.includes('recipient');
      const mentionsBank =
        descLower.includes('банк') ||
        descLower.includes('банк назначения') ||
        descLower.includes('destination bank');

      if (
        mentionsName &&
        mentionsBank &&
        dataValues.name &&
        dataValues.name.length > 0 &&
        dataValues.bank &&
        dataValues.bank.length > 0
      ) {
        copyableData = [dataValues.name, dataValues.bank];
      } else if (
        (descLower.includes('телефона') || descLower.includes('phone')) &&
        dataValues.tel &&
        dataValues.tel.length > 0
      ) {
        copyableData = dataValues.tel;
      } else if (
        (descLower.includes('номер карты') || descLower.includes('card')) &&
        dataValues.card &&
        dataValues.card.length > 0
      ) {
        copyableData = dataValues.card;
      } else if (
        (descLower.includes('номер телефона') ||
          descLower.includes('number')) &&
        dataValues.card &&
        dataValues.card.length > 0
      ) {
        // Fallback: many card instructions say "enter number"
        copyableData = dataValues.card;
      } else if (
        mentionsName &&
        dataValues.name &&
        dataValues.name.length > 0
      ) {
        copyableData = dataValues.name;
      } else if (
        mentionsBank &&
        dataValues.bank &&
        dataValues.bank.length > 0
      ) {
        copyableData = dataValues.bank;
      } else if (
        (descLower.includes('сумм') || descLower.includes('перевода')) &&
        dataValues.amount &&
        dataValues.amount.length > 0
      ) {
        copyableData = dataValues.amount;
      }

      const useMobileCoords =
        isMobile &&
        callout.xMobile !== undefined &&
        callout.yMobile !== undefined;

      return {
        id: callout.id,
        x: useMobileCoords ? callout.xMobile ?? callout.x : callout.x,
        y: useMobileCoords ? callout.yMobile ?? callout.y : callout.y,
        xMobile: callout.xMobile,
        yMobile: callout.yMobile,
        title: callout.title
          ? (t(callout.title) as string | undefined) || undefined
          : undefined,
        description: translatedDescription,
        copyableData,
        markerPosition: callout.markerPosition,
      };
    });
  }

  if (!isSbpMethod(method) || !bank) return [];

  const basePath = method === 'AMD_TG_SBP' ? 'amd-tg' : 'sbp';

  const bankName = bankMap[bank];
  const stepKey = String(step);

  // Navigate JSON structure: basePath -> bank -> device -> step
  const baseCallouts = paymentInstructionCallouts as Record<string, unknown>;
  const bankCallouts = (baseCallouts[basePath] as Record<string, unknown>)?.[
    bankName
  ] as Record<string, unknown> | undefined;
  const deviceCallouts = bankCallouts?.[device] as
    | Record<string, unknown>
    | undefined;
  const calloutsData = deviceCallouts?.[stepKey];

  if (!calloutsData || !Array.isArray(calloutsData)) {
    return [];
  }

  const interpolationVars: Record<string, string> = {};
  const dataValues: Record<string, string> = {};

  if (credentials) {
    const telValue = credentials.phoneNumber || credentials.value || '';
    interpolationVars.tel = telValue;
    dataValues.tel = telValue;

    const nameValue = credentials.name || '';
    interpolationVars.name = nameValue;
    dataValues.name = nameValue;

    const bankValue = credentials.bank || '';
    interpolationVars.bank = bankValue;
    dataValues.bank = bankValue;
  }

  if (amount && currency) {
    const amountValue = `${amount} ${currency}`;
    interpolationVars.amount = amountValue;
    dataValues.amount = amountValue;
  } else if (amount) {
    interpolationVars.amount = amount;
    dataValues.amount = amount;
  }

  return calloutsData.map((callout) => {
    const descriptionKey = callout.description;
    const translatedDescription = t(
      descriptionKey,
      interpolationVars,
    ) as string;

    let copyableData: string | string[] | undefined;

    const descLower = translatedDescription.toLowerCase();

    const mentionsName =
      descLower.includes('получател') ||
      descLower.includes('фио') ||
      descLower.includes('данные получателя') ||
      descLower.includes('recipient');
    const mentionsBank =
      descLower.includes('банк') ||
      descLower.includes('банк назначения') ||
      descLower.includes('destination bank');

    if (
      mentionsName &&
      mentionsBank &&
      dataValues.name &&
      dataValues.name.length > 0 &&
      dataValues.bank &&
      dataValues.bank.length > 0
    ) {
      // Return name and bank as separate copyable fields
      copyableData = [dataValues.name, dataValues.bank];
    } else if (
      (descLower.includes('телефона') || descLower.includes('номер')) &&
      dataValues.tel &&
      dataValues.tel.length > 0
    ) {
      copyableData = dataValues.tel;
    } else if (mentionsName && dataValues.name && dataValues.name.length > 0) {
      copyableData = dataValues.name;
    } else if (mentionsBank && dataValues.bank && dataValues.bank.length > 0) {
      copyableData = dataValues.bank;
    } else if (
      (descLower.includes('сумм') || descLower.includes('перевода')) &&
      dataValues.amount &&
      dataValues.amount.length > 0
    ) {
      copyableData = dataValues.amount;
    }

    // For mobile, use mobile-specific coordinates if available
    const useMobileCoords =
      isMobile &&
      callout.xMobile !== undefined &&
      callout.yMobile !== undefined;

    return {
      id: callout.id,
      x: useMobileCoords ? callout.xMobile ?? callout.x : callout.x,
      y: useMobileCoords ? callout.yMobile ?? callout.y : callout.y,
      xMobile: callout.xMobile,
      yMobile: callout.yMobile,
      title: callout.title
        ? (t(callout.title) as string | undefined) || undefined
        : undefined,
      description: translatedDescription,
      copyableData,
      markerPosition: callout.markerPosition,
    };
  });
}
